<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="handleCancel">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">Nouvelle facture proforma</h1>
          <p class="text-xs text-gray-400">Créez une proforma pour votre client</p>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="handleCancel">
          Annuler
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :disabled="!canCreate || isProcessing" @click="createProforma">
          <span v-if="isProcessing" class="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
          <template v-else>
            <i class="fas fa-check text-[10px]"></i>
            Créer la proforma
          </template>
        </button>
      </div>
    </div>

    <!-- Contenu principal -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">

      <!-- ===== COLONNE GAUCHE (2/3) ===== -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Carte Articles -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-visible">
          <div class="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <i class="fas fa-cart-shopping text-primary text-xs"></i>
              <h3 class="text-sm font-semibold text-gray-700">Articles</h3>
              <span class="text-[10px] text-gray-400 font-medium">({{ cart.length }})</span>
            </div>
            <button v-if="cart.length > 0"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              @click="clearCart">
              <i class="fas fa-trash text-[10px]"></i>
              Vider
            </button>
          </div>

          <div class="p-5">
            <!-- Barre de recherche produit -->
            <div class="relative mb-4" ref="productSearchContainer">
              <div class="relative">
                <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
                <input v-model="productSearch" type="text" placeholder="Rechercher un produit..."
                  class="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                  @input="searchProducts" @focus="showProductDropdown = true" @blur="hideProductDropdownDelayed" />
              </div>

              <!-- Dropdown produits -->
              <div
                v-if="showProductDropdown && (productSearching || productSearchResults.length > 0 || productSearch.length >= 2)"
                class="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
                <div v-if="productSearching" class="px-4 py-3 text-xs text-gray-400 flex items-center gap-2">
                  <i class="fas fa-spinner fa-spin"></i> Recherche...
                </div>
                <div v-else-if="productSearchResults.length === 0 && productSearch.length >= 2"
                  class="px-4 py-3 text-xs text-gray-400">
                  Aucun produit trouvé
                </div>
                <div v-for="product in productSearchResults" :key="product.id"
                  class="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50/60 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors"
                  @mousedown.prevent="addToCart(product)">
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-900 truncate">{{ product.name }}</p>
                    <p class="text-xs text-gray-400">{{ formatCurrency(product.price_excl_tax) }}</p>
                  </div>
                  <span class="ml-3 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    :class="product.stock_quantity > 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'">
                    {{ product.stock_quantity || 0 }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Panier vide -->
            <div v-if="cart.length === 0" class="flex flex-col items-center justify-center py-14 text-center px-6">
              <div class="text-gray-300 mb-3">
                <i class="fas fa-cart-shopping text-5xl"></i>
              </div>
              <h3 class="text-base font-semibold text-gray-700 mb-1">Le panier est vide</h3>
              <p class="text-sm text-gray-400 max-w-sm">Recherchez un produit ci-dessus pour commencer</p>
            </div>

            <!-- Liste articles (style flex comme SaleDetails) -->
            <div v-else class="divide-y divide-gray-50">
              <div v-for="(item, index) in cart" :key="item.product_id"
                class="flex items-center gap-4 py-3 hover:bg-gray-50/60 transition-colors -mx-5 px-5">
                <!-- Info produit -->
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 truncate">{{ item.name }}</p>
                  <p class="text-xs text-gray-400">
                    {{ formatCurrency(item.price_excl_tax) }} x {{ item.quantity }}
                    <span v-if="item.tax_rate > 0" class="ml-1">· TVA {{ item.tax_rate }}%</span>
                  </p>
                </div>
                <!-- Quantité -->
                <div class="flex items-center gap-1 flex-shrink-0">
                  <button
                    class="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    @click="updateQuantity(index, item.quantity - 1)" :disabled="item.quantity <= 1">
                    <i class="fas fa-minus text-[9px]"></i>
                  </button>
                  <input type="number" v-model.number="item.quantity" min="1"
                    class="w-10 h-7 text-center text-sm font-semibold text-gray-900 border border-gray-200 rounded-lg outline-none focus:border-primary transition-colors"
                    @change="updateCartTotals" />
                  <button
                    class="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
                    @click="updateQuantity(index, item.quantity + 1)">
                    <i class="fas fa-plus text-[9px]"></i>
                  </button>
                </div>
                <!-- Total -->
                <div class="text-sm font-bold text-gray-900 flex-shrink-0 w-24 text-right">
                  {{ formatCurrency(item.total) }}
                </div>
                <!-- Supprimer -->
                <button
                  class="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                  @click="removeFromCart(index)">
                  <i class="fas fa-times text-[10px]"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Totaux en bas de la carte (comme SaleDetails) -->
          <div v-if="cart.length > 0" class="px-5 py-4 border-t border-gray-100 space-y-2">
            <div class="flex justify-between text-sm">
              <span class="text-gray-500">Sous-total HT</span>
              <span class="font-medium text-gray-900">{{ formatCurrency(subtotalExclTax) }}</span>
            </div>
            <div v-if="totalTax > 0" class="flex justify-between text-sm">
              <span class="text-gray-500">TVA</span>
              <span class="font-medium text-gray-900">{{ formatCurrency(totalTax) }}</span>
            </div>
            <div v-if="discount > 0" class="flex justify-between text-sm">
              <span class="text-gray-500">Remise</span>
              <span class="font-medium text-red-500">-{{ formatCurrency(discount) }}</span>
            </div>
            <div class="flex justify-between items-center pt-2 border-t border-gray-100">
              <span class="text-sm font-bold text-gray-900">Total TTC</span>
              <span class="text-lg font-bold text-gray-900">{{ formatCurrency(totalInclTax) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== COLONNE DROITE (1/3) ===== -->
      <div class="space-y-5">

        <!-- Résumé rapide -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div :class="[
            'px-5 py-3 border-b',
            cart.length > 0 ? 'bg-primary/5 border-primary/10' : 'bg-gray-50 border-gray-100'
          ]">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <i :class="['fas fa-receipt text-xs', cart.length > 0 ? 'text-primary' : 'text-gray-400']"></i>
                <h3 :class="[
                  'text-sm font-semibold',
                  cart.length > 0 ? 'text-primary' : 'text-gray-500'
                ]">Résumé</h3>
              </div>
              <span v-if="cart.length > 0"
                class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                {{ cart.length }} article(s)
              </span>
            </div>
          </div>
          <div class="p-5 space-y-2.5">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Total HT</span>
              <span class="text-sm font-semibold text-gray-900">{{ formatCurrency(subtotalExclTax) }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">TVA</span>
              <span class="text-sm font-medium text-gray-900">{{ formatCurrency(totalTax) }}</span>
            </div>
            <div v-if="discount > 0" class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Remise</span>
              <span class="text-sm font-medium text-red-500">-{{ formatCurrency(discount) }}</span>
            </div>
            <div class="flex justify-between items-center pt-2.5 border-t border-gray-100">
              <span class="text-xs font-medium text-gray-500">Total TTC</span>
              <span class="text-lg font-bold text-gray-900">{{ formatCurrency(totalInclTax) }}</span>
            </div>

            <!-- Remise globale -->
            <div class="pt-3 border-t border-gray-100">
              <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1.5 block">Remise globale</label>
              <div class="relative">
                <i class="fas fa-percent absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-[10px]"></i>
                <input type="number" v-model.number="discount" min="0" step="1" placeholder="0 FCFA"
                  class="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                  @input="updateCartTotals" />
              </div>
            </div>
          </div>
        </div>

        <!-- Client -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-visible">
          <div class="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
            <i class="fas fa-user text-primary text-xs"></i>
            <h3 class="text-sm font-semibold text-gray-700">
              Client <span class="text-red-400">*</span>
            </h3>
          </div>
          <div class="p-5">
            <!-- Tabs -->
            <div class="flex gap-1 mb-4 p-0.5 bg-gray-100 rounded-lg">
              <button
                class="flex-1 py-1.5 text-xs font-medium rounded-md transition-colors"
                :class="customerType === 'existing' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
                @click="customerType = 'existing'">
                Existant
              </button>
              <button
                class="flex-1 py-1.5 text-xs font-medium rounded-md transition-colors"
                :class="customerType === 'new' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
                @click="customerType = 'new'">
                Nouveau
              </button>
            </div>

            <!-- Client existant -->
            <div v-if="customerType === 'existing'">
              <!-- Client sélectionné -->
              <div v-if="selectedCustomer"
                class="flex items-center gap-3 p-3 bg-primary/5 border border-primary/15 rounded-lg">
                <div
                  class="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-user text-primary text-[10px]"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 truncate">{{ selectedCustomer.name }}</p>
                  <p class="text-xs text-gray-400 truncate">{{ selectedCustomer.phone || selectedCustomer.email || '-' }}</p>
                </div>
                <button class="text-gray-300 hover:text-red-500 transition-colors p-1" @click="clearCustomer">
                  <i class="fas fa-times text-[10px]"></i>
                </button>
              </div>

              <!-- Recherche client -->
              <div v-else class="relative" ref="customerSearchContainer">
                <div class="relative">
                  <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
                  <input v-model="customerSearch" type="text" placeholder="Rechercher un client..."
                    class="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    @input="searchCustomers" @focus="showCustomerDropdown = true"
                    @blur="hideCustomerDropdownDelayed" />
                </div>

                <!-- Dropdown clients -->
                <div
                  v-if="showCustomerDropdown && (customersLoading || customers.length > 0 || customerSearch.length >= 2)"
                  class="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
                  <div v-if="customersLoading" class="px-4 py-3 text-xs text-gray-400 flex items-center gap-2">
                    <i class="fas fa-spinner fa-spin"></i> Recherche...
                  </div>
                  <div v-else-if="customers.length === 0 && customerSearch.length >= 2"
                    class="px-4 py-3 text-xs text-gray-400">
                    Aucun client trouvé
                  </div>
                  <div v-for="customer in customers" :key="customer.id"
                    class="px-4 py-2.5 hover:bg-gray-50/60 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors"
                    @mousedown.prevent="selectCustomer(customer)">
                    <p class="text-sm font-medium text-gray-900">{{ customer.name }}</p>
                    <p class="text-xs text-gray-400">{{ customer.phone || 'Pas de téléphone' }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Nouveau client -->
            <div v-if="customerType === 'new'" class="space-y-3">
              <div>
                <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">Nom <span class="text-red-400">*</span></label>
                <input v-model="newCustomer.name" type="text" placeholder="Nom du client"
                  class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" />
              </div>
              <div>
                <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">Téléphone</label>
                <input v-model="newCustomer.phone" type="text" placeholder="Téléphone"
                  class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" />
              </div>
            </div>

            <!-- Warning -->
            <div v-if="!hasValidCustomer"
              class="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg text-xs font-medium text-amber-600">
              <i class="fas fa-triangle-exclamation text-[10px]"></i>
              Client obligatoire
            </div>
          </div>
        </div>

        <!-- Informations -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
            <i class="fas fa-info-circle text-primary text-xs"></i>
            <h3 class="text-sm font-semibold text-gray-700">Informations</h3>
          </div>
          <div class="p-5 space-y-4">
            <div>
              <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">Date de validité</label>
              <input v-model="validityDate" type="date" :min="minValidityDate"
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" />
            </div>
            <div>
              <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">Notes</label>
              <textarea v-model="notes" rows="2" placeholder="Notes internes..."
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-y min-h-[48px]"></textarea>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import { proformaInvoiceService, productsService, customerService } from '@/services'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'CreateProformaInvoiceView',
  setup() {
    const router = useRouter()
    const toastStore = useToastStore()

    // État
    const isProcessing = ref(false)
    const customerType = ref('existing')
    const customerSearch = ref('')
    const customersLoading = ref(false)
    const selectedCustomerId = ref(null)
    const selectedCustomer = ref(null)
    const customers = ref([])
    const showCustomerDropdown = ref(false)
    const newCustomer = reactive({
      name: '',
      phone: ''
    })
    const cart = ref([])
    const discount = ref(0)
    const validityDate = ref('')
    const notes = ref('')

    // Recherche produits
    const productSearch = ref('')
    const productSearchResults = ref([])
    const productSearching = ref(false)
    const showProductDropdown = ref(false)
    const searchTimeout = ref(null)

    // Computed
    const hasValidCustomer = computed(() => {
      if (customerType.value === 'existing') {
        return !!selectedCustomerId.value
      }
      if (customerType.value === 'new') {
        return !!newCustomer.name.trim()
      }
      return false
    })

    const hasValidCart = computed(() => {
      return cart.value.length > 0
    })

    const subtotalExclTax = computed(() => {
      return cart.value.reduce((sum, item) => sum + (item.price_excl_tax * item.quantity), 0)
    })

    const totalTax = computed(() => {
      return cart.value.reduce((sum, item) => {
        const itemTax = (item.price_excl_tax * item.quantity * item.tax_rate) / 100
        return sum + itemTax
      }, 0)
    })

    const totalInclTax = computed(() => {
      const total = subtotalExclTax.value + totalTax.value
      return Math.max(0, total - discount.value)
    })

    const minValidityDate = computed(() => {
      const today = new Date()
      return today.toISOString().split('T')[0]
    })

    const canCreate = computed(() => {
      return hasValidCustomer.value && hasValidCart.value && !isProcessing.value
    })

    // Méthodes
    const hideProductDropdownDelayed = () => {
      setTimeout(() => {
        showProductDropdown.value = false
      }, 200)
    }

    const hideCustomerDropdownDelayed = () => {
      setTimeout(() => {
        showCustomerDropdown.value = false
      }, 200)
    }

    const searchProducts = async () => {
      if (!productSearch.value || productSearch.value.length < 2) {
        productSearchResults.value = []
        return
      }

      clearTimeout(searchTimeout.value)
      searchTimeout.value = setTimeout(async () => {
        try {
          productSearching.value = true
          const response = await productsService.getProducts({
            search: productSearch.value,
            per_page: 10
          })
          productSearchResults.value = response.data?.data || response.data || []
        } catch (error) {
          console.error('Erreur lors de la recherche de produits:', error)
          productSearchResults.value = []
        } finally {
          productSearching.value = false
        }
      }, 300)
    }

    const addToCart = (product) => {
      const existingIndex = cart.value.findIndex(item => item.product_id === product.id)

      if (existingIndex >= 0) {
        cart.value[existingIndex].quantity++
        updateCartTotals()
      } else {
        const itemTotalExclTax = product.price_excl_tax
        const itemTax = (itemTotalExclTax * (product.tax_rate || 0)) / 100

        cart.value.push({
          product_id: product.id,
          name: product.name,
          price_excl_tax: product.price_excl_tax,
          tax_rate: product.tax_rate || 0,
          quantity: 1,
          total: itemTotalExclTax + itemTax
        })
      }

      productSearch.value = ''
      productSearchResults.value = []
      showProductDropdown.value = false
    }

    const removeFromCart = (index) => {
      cart.value.splice(index, 1)
    }

    const clearCart = () => {
      cart.value = []
    }

    const updateQuantity = (index, newQuantity) => {
      if (newQuantity < 1) return
      cart.value[index].quantity = newQuantity
      updateCartTotals()
    }

    const updateCartTotals = () => {
      cart.value.forEach(item => {
        const itemTotalExclTax = item.price_excl_tax * item.quantity
        const itemTax = (itemTotalExclTax * item.tax_rate) / 100
        item.total = itemTotalExclTax + itemTax
      })
    }

    const searchCustomers = async () => {
      if (!customerSearch.value || customerSearch.value.length < 2) {
        customers.value = []
        return
      }

      clearTimeout(searchTimeout.value)
      searchTimeout.value = setTimeout(async () => {
        try {
          customersLoading.value = true
          const response = await customerService.getCustomers({
            search: customerSearch.value,
            per_page: 10
          })
          customers.value = response.data?.data || response.data || []
        } catch (error) {
          console.error('Erreur lors de la recherche de clients:', error)
        } finally {
          customersLoading.value = false
        }
      }, 300)
    }

    const selectCustomer = (customer) => {
      selectedCustomerId.value = customer.id
      selectedCustomer.value = customer
      customers.value = []
      customerSearch.value = ''
      showCustomerDropdown.value = false
    }

    const clearCustomer = () => {
      selectedCustomerId.value = null
      selectedCustomer.value = null
      customerSearch.value = ''
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const handleCancel = () => {
      if (cart.value.length > 0 || notes.value) {
        if (confirm('Êtes-vous sûr de vouloir annuler ? Les modifications seront perdues.')) {
          router.push({ name: 'proforma-invoices' })
        }
      } else {
        router.push({ name: 'proforma-invoices' })
      }
    }

    const createProforma = async () => {
      if (!canCreate.value || isProcessing.value) return

      isProcessing.value = true

      try {
        const items = cart.value.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price_excl_tax: item.price_excl_tax
        }))

        const proformaData = {
          items,
          discount: discount.value || 0
        }

        if (customerType.value === 'existing' && selectedCustomerId.value) {
          proformaData.customer_id = selectedCustomerId.value
        } else if (customerType.value === 'new' && newCustomer.name) {
          proformaData.customer = {
            name: newCustomer.name,
            phone: newCustomer.phone || null
          }
        }

        if (validityDate.value) {
          proformaData.validity_date = validityDate.value
        }
        if (notes.value) {
          proformaData.notes = notes.value
        }

        await proformaInvoiceService.createProformaInvoice(proformaData)

        toastStore.addToast({
          type: 'success',
          message: 'Facture proforma créée avec succès',
          duration: 4000
        })

        router.push({ name: 'proforma-invoices' })
      } catch (error) {
        console.error('Erreur lors de la création de la proforma:', error)
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors de la création de la proforma',
          duration: 4000
        })
      } finally {
        isProcessing.value = false
      }
    }

    return {
      isProcessing,
      customerType,
      customerSearch,
      customersLoading,
      selectedCustomerId,
      selectedCustomer,
      customers,
      showCustomerDropdown,
      newCustomer,
      cart,
      discount,
      validityDate,
      notes,
      productSearch,
      productSearchResults,
      productSearching,
      showProductDropdown,
      hasValidCustomer,
      hasValidCart,
      subtotalExclTax,
      totalTax,
      totalInclTax,
      minValidityDate,
      canCreate,
      hideProductDropdownDelayed,
      hideCustomerDropdownDelayed,
      searchProducts,
      addToCart,
      removeFromCart,
      clearCart,
      updateQuantity,
      updateCartTotals,
      searchCustomers,
      selectCustomer,
      clearCustomer,
      formatCurrency,
      handleCancel,
      createProforma
    }
  }
})
</script>

<style scoped>
/* Masquer les flèches des inputs number */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}
</style>
